import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from './react';
import { ensureNamespace, isNamespaceReady, retainNamespace } from './runtime';

/** Show product copy only after this module's selected catalog is available. */
export function NamespaceBoundary({ namespace, children, className = 'flex min-h-40 items-center justify-center p-6' }: { namespace: string; children: ReactNode; className?: string }) {
  const { locale, t } = useTranslation();
  const [attempt, retry] = useState(0);
  const [failure, setFailure] = useState<string | null>(null);
  const identity = `${namespace}:${locale}:${attempt}`;
  useEffect(() => {
    let current = true;
    const release = retainNamespace(namespace);
    void ensureNamespace(namespace, locale).catch(() => {
      if (current) setFailure(identity);
    });
    return () => { current = false; release(); };
  }, [namespace, locale, identity]);

  if (isNamespaceReady(namespace, locale)) return children;
  return (
    <div className={className}>
      {failure === identity ? (
        <div className="space-y-3 text-center">
          <p role="alert">{t('language.failed')}</p>
          <button type="button" className="text-blaze-orange" onClick={() => retry((value) => value + 1)}>{t('retry')}</button>
        </div>
      ) : <p role="status">{t('loading')}</p>}
    </div>
  );
}
